import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ShowCard from './ShowCard';
import useRetryOnReconnect from './useRetryOnReconnect';

const PREVIEW_COUNT = 5;

export default function HomeShowsPreview() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const scrollRef = useRef(null);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [showLeftNav, setShowLeftNav] = useState(false);
  const [showRightNav, setShowRightNav] = useState(true);

  const fetchShows = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://broadcast.wssr.stream/api/station/1/public/podcasts');
      setShows(response.data.slice(0, PREVIEW_COUNT));
      setHasError(false);
    } catch (error) {
      console.error('Error fetching shows preview:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShows();
  }, []);

  useRetryOnReconnect(fetchShows, hasError);

  const checkScrollPosition = () => {
    if (!scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

    setShowLeftNav(Math.round(scrollLeft) > 35);
    setShowRightNav(Math.round(scrollLeft) < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    requestAnimationFrame(() => {
      checkScrollPosition();
    });

    scrollElement.addEventListener('scroll', checkScrollPosition);

    return () => scrollElement.removeEventListener('scroll', checkScrollPosition);
  }, [shows]);

  const handleNextScroll = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const handlePrevScroll = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const items = (loading || (hasError && shows.length === 0)) ? Array.from(new Array(PREVIEW_COUNT)) : shows;

  return (
    <Box sx={{ marginTop: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ marginBottom: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Shows</Typography>
        <Link to="/shows" style={{ color: 'inherit', fontSize: '0.85rem', opacity: 0.75 }}>
          See All
        </Link>
      </Box>

      <Box sx={{ position: 'relative', mx: { xs: -2, md: 0 }, px: { xs: 2, md: 0 } }}>
        {isDesktop && (
          <IconButton
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: '#fff',
              borderRadius: '0%',
              zIndex: 3,
              opacity: showLeftNav ? 1 : 0,
              visibility: showLeftNav ? 'visible' : 'hidden',
              transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
            }}
            onClick={handlePrevScroll}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        )}

        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            paddingBottom: 1,
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {items.map((show, index) => (
            <Box
              key={show?.id || index}
              sx={{ width: 200, height: 260, flexShrink: 0, scrollSnapAlign: 'start' }}
            >
              <ShowCard id={show?.id} title={show?.title} description={show?.description} image={show?.art} compact />
            </Box>
          ))}
        </Box>

        {isDesktop && (
          <IconButton
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: '#fff',
              borderRadius: '0%',
              zIndex: 3,
              opacity: showRightNav ? 1 : 0,
              visibility: showRightNav ? 'visible' : 'hidden',
              transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
            }}
            onClick={handleNextScroll}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
